import React, { useState } from 'react';
import { PlusIcon, GripVerticalIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FormField } from './FormField';
export interface CaptureField {
  id: string;
  type: 'preset' | 'custom';
  preset?: 'name' | 'email' | 'phone' | 'address' | 'company';
  label?: string;
  description?: string;
  required: boolean;
}
interface CaptureFieldsEditorProps {
  fields: CaptureField[];
  onChange: (fields: CaptureField[]) => void;
}
export function CaptureFieldsEditor({
  fields,
  onChange
}: CaptureFieldsEditorProps) {
  const [newFieldType, setNewFieldType] = useState<'preset' | 'custom'>('preset');
  const [newFieldPreset, setNewFieldPreset] = useState<'name' | 'email' | 'phone' | 'address' | 'company'>('name');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const handleAddField = () => {
    if (newFieldType === 'preset') {
      // Check if this preset already exists
      const presetExists = fields.some(field => field.type === 'preset' && field.preset === newFieldPreset);
      if (presetExists) {
        alert(`A field for "${getPresetLabel(newFieldPreset)}" already exists.`);
        return;
      }
      const newField: CaptureField = {
        id: `preset-${newFieldPreset}-${Date.now()}`,
        type: 'preset',
        preset: newFieldPreset,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
    } else if (newFieldType === 'custom') {
      if (!newFieldLabel.trim()) {
        alert('Please enter a label for the custom field.');
        return;
      }
      const newField: CaptureField = {
        id: `custom-${Date.now()}`,
        type: 'custom',
        label: newFieldLabel,
        description: newFieldDescription,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
      setNewFieldLabel('');
      setNewFieldDescription('');
    }
    setShowAddForm(false);
  };
  const handleRemoveField = (fieldId: string) => {
    onChange(fields.filter(field => field.id !== fieldId));
  };
  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === fields.length - 1) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex];
    newFields[targetIndex] = temp;
    onChange(newFields);
  };
  const handleToggleRequired = (fieldId: string) => {
    onChange(fields.map(field => field.id === fieldId ? {
      ...field,
      required: !field.required
    } : field));
  };
  const getPresetLabel = (preset: 'name' | 'email' | 'phone' | 'address' | 'company'): string => {
    const labels = {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      address: 'Billing Address',
      company: 'Company Name'
    };
    return labels[preset];
  };
  return <div className="space-y-4" data-id="element-467">
      <div className="flex justify-between items-center mb-2" data-id="element-468">
        <h3 className="text-sm font-medium text-gray-700" data-id="element-469">
          Customer Information Fields
        </h3>
        <button type="button" onClick={() => setShowAddForm(!showAddForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-470">
          {showAddForm ? 'Cancel' : <>
              <PlusIcon size={14} className="mr-1" data-id="element-471" />
              Add Field
            </>}
        </button>
      </div>
      {/* Add new field form */}
      {showAddForm && <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4" data-id="element-472">
          <div className="mb-3" data-id="element-473">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-474">
              Field Type
            </label>
            <div className="flex space-x-3" data-id="element-475">
              <label className="flex items-center" data-id="element-476">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'preset'} onChange={() => setNewFieldType('preset')} data-id="element-477" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-478">Preset Field</span>
              </label>
              <label className="flex items-center" data-id="element-479">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'custom'} onChange={() => setNewFieldType('custom')} data-id="element-480" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-481">Custom Field</span>
              </label>
            </div>
          </div>
          {newFieldType === 'preset' ? <div className="mb-3" data-id="element-482">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-483">
                Preset Field
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={newFieldPreset} onChange={e => setNewFieldPreset(e.target.value as any)} data-id="element-484">
                <option value="name" data-id="element-485">Full Name</option>
                <option value="email" data-id="element-486">Email Address</option>
                <option value="phone" data-id="element-487">Phone Number</option>
                <option value="address" data-id="element-488">Billing Address</option>
                <option value="company" data-id="element-489">Company Name</option>
              </select>
            </div> : <>
              <div className="mb-3" data-id="element-490">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-491">
                  Field Label
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Project Name" value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} data-id="element-492" />
              </div>
              <div className="mb-3" data-id="element-493">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-494">
                  Field Description (Optional)
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Please provide the project name for your invoice" value={newFieldDescription} onChange={e => setNewFieldDescription(e.target.value)} data-id="element-495" />
              </div>
            </>}
          <div className="mb-3" data-id="element-496">
            <label className="flex items-center" data-id="element-497">
              <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={newFieldRequired} onChange={e => setNewFieldRequired(e.target.checked)} data-id="element-498" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-499">Required field</span>
            </label>
          </div>
          <div className="flex justify-end" data-id="element-500">
            <button type="button" onClick={handleAddField} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" data-id="element-501">
              Add Field
            </button>
          </div>
        </div>}
      {/* List of fields */}
      {fields.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-502">
          {fields.map((field, index) => <div key={field.id} className="p-3 flex items-center" data-id="element-503">
              <div className="flex-shrink-0 mr-2 text-gray-400" data-id="element-504">
                <GripVerticalIcon size={16} data-id="element-505" />
              </div>
              <div className="flex-1" data-id="element-506">
                <div className="flex justify-between items-center" data-id="element-507">
                  <div data-id="element-508">
                    <span className="font-medium text-gray-800" data-id="element-509">
                      {field.type === 'preset' ? getPresetLabel(field.preset!) : field.label}
                    </span>
                    {field.required && <span className="ml-1 text-red-500 text-xs" data-id="element-510">*</span>}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-511">
                    <button type="button" onClick={() => handleToggleRequired(field.id)} className={`text-xs px-2 py-1 rounded ${field.required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-512">
                      {field.required ? 'Required' : 'Optional'}
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'up')} disabled={index === 0} className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-513">
                      <ChevronUpIcon size={16} data-id="element-514" />
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'down')} disabled={index === fields.length - 1} className={`text-gray-400 hover:text-gray-600 ${index === fields.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-515">
                      <ChevronDownIcon size={16} data-id="element-516" />
                    </button>
                    <button type="button" onClick={() => handleRemoveField(field.id)} className="text-gray-400 hover:text-red-500" data-id="element-517">
                      <XIcon size={16} data-id="element-518" />
                    </button>
                  </div>
                </div>
                {field.type === 'custom' && field.description && <p className="text-xs text-gray-500 mt-1" data-id="element-519">
                    {field.description}
                  </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-520">
                  {field.type === 'preset' ? 'Standard input field' : 'Custom input field'}
                </p>
              </div>
            </div>)}
        </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-521">
          <p className="text-gray-500" data-id="element-522">No capture fields added</p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-523">
            Add fields to collect information from your customers
          </p>
        </div>}
      <div className="text-xs text-gray-500 mt-2" data-id="element-524">
        These fields will be shown on the payment page for customers to fill out
        before completing their payment.
      </div>
    </div>;
}